import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, Eye, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import DOMPurify from 'dompurify';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * TemplateSelector - Reusable component for selecting email templates by type
 *
 * @param {string} templateType - Type of templates to show (e.g., 'rfq_inclusion_request')
 * @param {string} label - Label for the selector button (e.g., 'Email Template')
 * @param {Function} onTemplateSelect - Callback when template is selected (template object)
 * @param {string} selectedTemplateId - Currently selected template ID
 * @param {string} variant - Button variant ('default', 'outline', 'ghost')
 * @param {string} size - Button size ('sm', 'md', 'lg')
 */
const TemplateSelector = ({
  templateType,
  label = 'Email Template',
  onTemplateSelect,
  selectedTemplateId = null,
  variant = 'outline',
  size = 'sm'
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [templateType]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/matching/email-templates?type=${templateType}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTemplates(data.templates || []);

        // Auto-select default template if nothing selected
        if (!selectedTemplateId && data.templates.length > 0) {
          const defaultTemplate = data.templates.find(t => t.is_default) || data.templates[0];
          if (onTemplateSelect) {
            onTemplateSelect(defaultTemplate);
          }
        }
      } else {
        throw new Error(data.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const handlePreview = (template, e) => {
    e.stopPropagation(); // Prevent dropdown from closing
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  if (error) {
    return (
      <div className="text-sm text-red-600">
        Error loading templates: {error}
      </div>
    );
  }

  if (loading) {
    return (
      <Button variant={variant} size={size} disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading templates...
      </Button>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No templates available for this action
      </div>
    );
  }

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant={variant} size={size}>
            <FileText className="h-4 w-4 mr-2" />
            {selectedTemplate ? selectedTemplate.name : label}
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Select Email Template</h4>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className="flex items-start justify-between cursor-pointer p-3 hover:bg-muted transition-colors border-b last:border-b-0"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{template.name}</span>
                    {template.is_default && (
                      <Badge variant="secondary" className="text-xs">Default</Badge>
                    )}
                    {template.id === selectedTemplateId && (
                      <Badge className="text-xs bg-primary">Selected</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {template.subject_template}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 w-6 p-0"
                  onClick={(e) => handlePreview(template, e)}
                >
                  <Eye className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Template Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {previewTemplate?.name}
              {previewTemplate?.is_default && (
                <Badge variant="secondary" className="text-xs">Default</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Template preview with merge tags (will be replaced with actual values)
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Subject</label>
                <div className="bg-muted p-3 rounded-md text-sm">
                  {previewTemplate.subject_template}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Body</label>
                <div
                  className="bg-muted p-3 rounded-md text-sm max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewTemplate.body_template) }}
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-xs text-muted-foreground">
                  Type: <code className="bg-muted px-1 py-0.5 rounded">{previewTemplate.type}</code>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowPreview(false)}>
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      handleTemplateSelect(previewTemplate);
                      setShowPreview(false);
                    }}
                  >
                    Use This Template
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemplateSelector;
